/**
 * Elite Batch 22 Part 1 — WebEngage Marketing Workflows (1-13)
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers have EMPTY inputFields — an AI extraction node follows
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Node names must be descriptive (e.g., "WebEngage: Fetch user profile", not "API Call")
 * 7. Each workflow needs 8 nodes with proper edges
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 22 PART 1 — WebEngage Marketing Workflows (1-13)
// =============================================================================

export const ELITE_BATCH22_PART1: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1. USER ONBOARDING JOURNEY — Pattern A
  // ===================================================================
  "User Onboarding Journey": {
    nodes: [
      trigger("WebEngage: New User Signup Webhook", "webhook_app", {
        source: "WebEngage",
        event: "user.signup",
        howItStarts:
          "Fires when a new user completes signup in your app or website, captured by WebEngage SDK tracking",
        setup:
          "In WebEngage Dashboard > Integrations > Webhooks, create a webhook that fires on the 'User Signed Up' system event. Ensure user_id, email, name, and signup_source are included in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract signup details and personalization cues",
        280,
        "You are a user onboarding data extractor. Parse the incoming webhook payload and extract structured user signup details. Determine the best onboarding path based on signup source and any available profile data.",
        "Extract from this WebEngage signup webhook: {{{1.body}}}. Return JSON with fields: user_email, user_name, signup_source (web/app/social), device_type, preferred_language, is_referred (boolean), onboarding_priority (high/medium/low).",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "device_type", as: "device_type", type: "string" },
          { field: "is_referred", as: "is_referred", type: "boolean" },
          {
            field: "onboarding_priority",
            as: "onboarding_priority",
            type: "string",
          },
        ],
      ),

      n("3", "hitApi", "HubSpot: Check if returning customer", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{user_email}"}]}]',
            },
          ],
          variables: [
            {
              name: "total",
              customName: "existing_contact_count",
              type: "number",
            },
            {
              name: "results[0].properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "results[0].properties.hs_object_id",
              customName: "hubspot_contact_id",
              type: "string",
            },
          ],
        },
      }),

      n("4", "condition", "Is this a returning customer?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "existing_contact_count",
              operator: "greaterThan",
              value: "0",
            },
          ],
        },
      }),

      emailNode(
        "5",
        "SendGrid: Welcome email with value proposition",
        1000,
        "{user_email}",
        "Welcome to the family, {user_name}! Here's how to get started",
        "Hi {user_name},\n\nWelcome aboard! We're thrilled to have you join us.\n\nHere are 3 things you can do right now to get the most value:\n\n1. Complete your profile to unlock personalized recommendations\n2. Explore our Quick Start Guide to set up your first project\n3. Join our community forum to connect with other users\n\nYour signup source: {signup_source}\n\nNeed help? Reply to this email or visit our Help Center.\n\nCheers,\nThe Team",
      ),

      n("6", "delayAction", "Wait 24 hours for activation", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      n("7", "condition", "Has user activated key feature?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "lifecycle_stage", operator: "equals", value: "customer" },
          ],
        },
      }),

      emailNode(
        "8",
        "SendGrid: Activation nudge with tutorial link",
        1720,
        "{user_email}",
        "{user_name}, you're one step away from unlocking the full experience",
        "Hi {user_name},\n\nWe noticed you haven't completed your first key action yet. Don't worry — it only takes 2 minutes!\n\nHere's a quick walkthrough to help you get started:\n[Watch the 90-second tutorial]\n\nMost users who complete this step within 48 hours see 3x more value in their first week.\n\nNeed a hand? Book a free 15-minute onboarding call with our team.\n\nBest,\nThe Onboarding Team",
        340,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Automated onboarding journey triggered by WebEngage signup events. Extracts user details via AI, checks HubSpot for returning customers, sends a personalized welcome email, waits 24 hours, then checks activation status and sends a follow-up nudge with tutorial content for users who haven't completed their first key action.",
  },

  // ===================================================================
  // 2. APP ACTIVATION NUDGE — Pattern A
  // ===================================================================
  "App Activation Nudge": {
    nodes: [
      trigger("WebEngage: App Install Detected", "webhook_app", {
        source: "WebEngage",
        event: "app.installed",
        howItStarts:
          "Fires when a user installs the mobile app and opens it for the first time, tracked by WebEngage mobile SDK",
        setup:
          "In WebEngage Dashboard > Integrations > Webhooks, create a webhook for the 'App Installed' event. Ensure device_id, user_id, app_version, os_type, and install_source are forwarded.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract install context and recommend activation path",
        280,
        "You are a mobile app activation specialist. Parse app install webhook data and determine the optimal activation path based on device type, install source, and any available user context.",
        "Extract from this WebEngage app install webhook: {{{1.body}}}. Return JSON with: user_id, user_email, device_os (ios/android), app_version, install_source (organic/paid/referral), has_account (boolean), recommended_first_action (string describing the ideal first feature to try).",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "device_os", as: "device_os", type: "string" },
          { field: "install_source", as: "install_source", type: "string" },
          { field: "has_account", as: "has_account", type: "boolean" },
          {
            field: "recommended_first_action",
            as: "recommended_first_action",
            type: "string",
          },
        ],
      ),

      n(
        "3",
        "hitApi",
        "WebEngage: Fetch user profile and engagement data",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.webengage.com/v2/users/{user_id}",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            variables: [
              {
                name: "user.email",
                customName: "profile_email",
                type: "string",
              },
              {
                name: "user.firstName",
                customName: "first_name",
                type: "string",
              },
              {
                name: "user.attributes.activated",
                customName: "is_activated",
                type: "boolean",
              },
              {
                name: "user.attributes.onboarding_step",
                customName: "onboarding_step",
                type: "number",
              },
            ],
          },
        },
      ),

      n("4", "condition", "Has user already activated a key feature?", 760, {
        conditionDetails: {
          conditions: [
            { field: "is_activated", operator: "equals", value: "true" },
          ],
        },
      }),

      n("5", "hitApi", "WebEngage: Send personalized push notification", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.webengage.com/v2/users/{user_id}/push",
          headers: [
            { key: "Authorization", value: "Bearer {webengage_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "Hey {first_name}, ready to explore?" },
            {
              key: "message",
              value:
                "Try {recommended_first_action} — most users love starting here! Tap to get started in under 60 seconds.",
            },
            { key: "deepLink", value: "app://activation/quickstart" },
            { key: "ttl", value: "86400" },
          ],
          variables: [
            {
              name: "response.status",
              customName: "push_status",
              type: "string",
            },
            {
              name: "response.messageId",
              customName: "push_message_id",
              type: "string",
            },
          ],
        },
      }),

      n("6", "delayAction", "Wait 48 hours for feature activation", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),

      n("7", "condition", "Still not activated after 48h?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "onboarding_step", operator: "lessThan", value: "2" },
          ],
        },
      }),

      emailNode(
        "8",
        "SendGrid: Feature activation email with step-by-step guide",
        1720,
        "{user_email}",
        "{first_name}, unlock the best of the app in 3 easy steps",
        "Hi {first_name},\n\nWe noticed you installed the app but haven't tried {recommended_first_action} yet. Here's a quick guide:\n\nStep 1: Open the app and tap the Explore tab\nStep 2: Select '{recommended_first_action}' from the feature list\nStep 3: Follow the interactive walkthrough (takes ~90 seconds)\n\nUsers who activate this feature in their first week report 4x higher satisfaction!\n\nDevice: {device_os} | Install source: {install_source}\n\nStuck? Our support team is available 24/7 in the app chat.\n\nBest,\nThe App Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Mobile app activation nudge workflow triggered when WebEngage detects a new app install. Uses AI to determine the best activation path, checks the user's current engagement status, sends a targeted push notification to drive first feature usage, then follows up via email after 48 hours if the user still hasn't activated a key feature.",
  },

  // ===================================================================
  // 3. WEB PUSH WELCOME — Pattern A
  // ===================================================================
  "Web Push Welcome": {
    nodes: [
      trigger("WebEngage: Push Notification Opt-In", "webhook_app", {
        source: "WebEngage",
        event: "user.push_optin",
        howItStarts:
          "Fires when a website visitor opts in to receive web push notifications through the WebEngage push prompt",
        setup:
          "In WebEngage Dashboard > Integrations > Webhooks, create a webhook for the 'Push Opt-In' event. The payload will include browser details, user_id (if logged in), and the subscription token.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract opt-in context and visitor profile",
        280,
        "You are a web push notification specialist. Parse the push opt-in webhook and extract visitor context to personalize the welcome push sequence.",
        "Extract from this WebEngage push opt-in webhook: {{{1.body}}}. Return JSON with: user_id, user_email (if available, else empty), browser_type, device_type (desktop/mobile/tablet), referral_page (the page they opted in from), visitor_segment (new/returning/registered), locale.",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "user_email", as: "user_email", type: "string" },
          { field: "browser_type", as: "browser_type", type: "string" },
          { field: "referral_page", as: "referral_page", type: "string" },
          { field: "visitor_segment", as: "visitor_segment", type: "string" },
          { field: "locale", as: "locale", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "WebEngage: Lookup visitor profile and browsing history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.webengage.com/v2/users/{user_id}",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            variables: [
              {
                name: "user.firstName",
                customName: "visitor_name",
                type: "string",
              },
              {
                name: "user.attributes.total_visits",
                customName: "total_visits",
                type: "number",
              },
              {
                name: "user.attributes.email_opted_in",
                customName: "has_email_optin",
                type: "boolean",
              },
              {
                name: "user.attributes.last_category_viewed",
                customName: "last_category",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "condition",
        "Is visitor a returning user with known preferences?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "total_visits", operator: "greaterThan", value: "3" },
            ],
          },
        },
      ),

      n(
        "5",
        "hitApi",
        "WebEngage: Send welcome push with personalized content",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/users/{user_id}/push",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "title", value: "Welcome! You're now in the loop" },
              {
                key: "message",
                value:
                  "Thanks for subscribing to push notifications. You'll be the first to know about deals, new arrivals in {last_category}, and exclusive offers!",
              },
              {
                key: "url",
                value: "https://yourstore.com/welcome-subscriber?ref=push",
              },
              {
                key: "icon",
                value: "https://yourstore.com/assets/push-icon.png",
              },
              { key: "ttl", value: "43200" },
            ],
            variables: [
              {
                name: "response.status",
                customName: "welcome_push_status",
                type: "string",
              },
              {
                name: "response.messageId",
                customName: "welcome_push_id",
                type: "string",
              },
            ],
          },
        },
      ),

      n("6", "delayAction", "Wait 36 hours before email fallback check", 1240, {
        delayDetails: { duration: 36, unit: "hours" },
      }),

      n("7", "condition", "Does user also have email opt-in?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "has_email_optin", operator: "equals", value: "false" },
          ],
        },
      }),

      emailNode(
        "8",
        "SendGrid: Email fallback with email opt-in request",
        1720,
        "{user_email}",
        "Stay connected — get the best of both channels",
        "Hi {visitor_name},\n\nThanks for subscribing to our push notifications! You're already in the loop for the latest deals.\n\nWant to make sure you never miss out? Subscribe to our email newsletter too for:\n- Exclusive email-only discounts\n- Weekly curated picks in {last_category}\n- Early access to seasonal sales\n\n[Subscribe to Email Updates]\n\nBrowser: {browser_type} | Visits: {total_visits}\n\nYou're in control — manage your preferences anytime.\n\nBest,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Web push welcome journey triggered when a visitor opts in to push notifications via WebEngage. AI extracts visitor context, WebEngage looks up browsing history, then sends a personalized welcome push referencing their interests. After 36 hours, checks if the user lacks email opt-in and sends an email encouraging cross-channel subscription.",
  },

  // ===================================================================
  // 4. CART ABANDONMENT RECOVERY — Pattern B
  // ===================================================================
  "Cart Abandonment Recovery": {
    nodes: [
      trigger("WebEngage: Cart Abandoned Event", "webhook_app", {
        source: "WebEngage",
        event: "cart.abandoned",
        howItStarts:
          "Fires when a user adds items to cart but does not complete checkout within 30 minutes, detected by WebEngage event tracking",
        setup:
          "In WebEngage Dashboard > Integrations > Webhooks, create a webhook for the 'Checkout Abandoned' custom event. Ensure cart_id, user_id, product data, and cart_total are included in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract cart items and generate recovery messaging",
        280,
        "You are an ecommerce cart recovery specialist. Parse the abandoned cart webhook, extract product details, and generate persuasive recovery copy tailored to the cart contents and value.",
        "Extract from this WebEngage cart abandonment webhook: {{{1.body}}}. Return JSON with: user_email, user_name, cart_id, cart_total (number), cart_item_count (number), top_product_name (most expensive item), top_product_image_url, top_product_price (number), recovery_subject_line (compelling email subject), recovery_hook (one-line urgency message).",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "cart_id", as: "cart_id", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "cart_item_count", as: "cart_item_count", type: "number" },
          { field: "top_product_name", as: "top_product_name", type: "string" },
          {
            field: "top_product_price",
            as: "top_product_price",
            type: "number",
          },
          {
            field: "recovery_subject_line",
            as: "recovery_subject_line",
            type: "string",
          },
          { field: "recovery_hook", as: "recovery_hook", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "Shopify: Verify cart contents and stock availability",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/checkouts/{cart_id}.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            ],
            variables: [
              {
                name: "checkout.total_price",
                customName: "verified_cart_total",
                type: "string",
              },
              {
                name: "checkout.line_items[0].title",
                customName: "primary_item_title",
                type: "string",
              },
              {
                name: "checkout.abandoned_checkout_url",
                customName: "checkout_recovery_url",
                type: "string",
              },
              {
                name: "checkout.line_items.length",
                customName: "verified_item_count",
                type: "number",
              },
            ],
          },
        },
      ),

      n("4", "condition", "Is cart value above $100 (high value)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "cart_total", operator: "greaterThan", value: "100" },
          ],
        },
      }),

      emailNode(
        "5",
        "SendGrid: First cart recovery email with product details",
        1000,
        "{user_email}",
        "{recovery_subject_line}",
        "Hi {user_name},\n\n{recovery_hook}\n\nYou left {cart_item_count} item(s) in your cart worth ${cart_total}:\n\nFeatured item: {top_product_name} — ${top_product_price}\n\n[Complete Your Purchase]\n{checkout_recovery_url}\n\nYour cart is saved, but stock is limited. Don't let your favorites slip away!\n\nFree shipping on orders over $75.\n\nNeed help? Our support team is here for you.\n\nBest,\nThe Store Team",
      ),

      n("6", "delayAction", "Wait 12 hours before follow-up", 1240, {
        delayDetails: { duration: 12, unit: "hours" },
      }),

      n("7", "condition", "Has user still not completed purchase?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "verified_item_count",
              operator: "greaterThan",
              value: "0",
            },
          ],
        },
      }),

      n(
        "8",
        "hitApi",
        "WebEngage: Send SMS with exclusive discount code",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/users/{user_email}/sms",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "message",
                value:
                  "Hi {user_name}! Your cart (${cart_total}) is waiting. Use code SAVE10 for 10% off. Complete your order: {checkout_recovery_url}",
              },
              { key: "senderName", value: "YourStore" },
            ],
            variables: [
              {
                name: "response.status",
                customName: "sms_status",
                type: "string",
              },
              {
                name: "response.messageId",
                customName: "sms_message_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Cart abandonment recovery workflow using WebEngage omnichannel capabilities. AI extracts cart details and generates personalized recovery copy, Shopify verifies stock and provides a recovery URL, then sends a first email reminder. After 12 hours, if the purchase is still incomplete, escalates to SMS with a 10% discount incentive code.",
  },

  // ===================================================================
  // 5. BROWSE ABANDONMENT REMINDER — Pattern C
  // ===================================================================
  "Browse Abandonment Reminder": {
    nodes: [
      trigger("WebEngage: Product Viewed Without Cart Add", "webhook_app", {
        source: "WebEngage",
        event: "product.viewed_no_cart",
        howItStarts:
          "Fires when a user views a product page at least twice but does not add to cart within a session, tracked by WebEngage behavioral events",
        setup:
          "In WebEngage Dashboard > Integrations > Webhooks, configure a webhook for the 'Product Viewed' event with a filter: event count >= 2 AND no 'Added to Cart' event within the same session. Include user_id, product data, and session details.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract viewed products and infer purchase intent",
        280,
        "You are a browse behavior analyst. Parse the browse abandonment webhook, extract product details, and determine the user's likely purchase intent and best re-engagement angle.",
        "Extract from this WebEngage browse abandonment webhook: {{{1.body}}}. Return JSON with: user_email, user_name, product_name, product_category, product_price (number), product_url, product_image_url, view_count (number), browse_intent (high/medium/low), recommended_angle (social_proof/scarcity/value/comparison).",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "view_count", as: "view_count", type: "number" },
          {
            field: "recommended_angle",
            as: "recommended_angle",
            type: "string",
          },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Lookup customer affinity and purchase history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{user_email}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["email","firstname","total_revenue","num_associated_deals","hs_buying_role"]',
              },
            ],
            variables: [
              { name: "total", customName: "contact_found", type: "number" },
              {
                name: "results[0].properties.total_revenue",
                customName: "customer_ltv",
                type: "string",
              },
              {
                name: "results[0].properties.num_associated_deals",
                customName: "past_purchases",
                type: "number",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "condition",
        "Is this a returning customer with purchase history?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "past_purchases", operator: "greaterThan", value: "0" },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "SendGrid: Browse reminder with viewed product details",
        1000,
        "{user_email}",
        "Still thinking about {product_name}? Here's why customers love it",
        "Hi {user_name},\n\nWe noticed you've been eyeing {product_name} (${product_price}).\n\nHere's what makes it special:\n- Top rated in {product_category}\n- Viewed {view_count} times in your recent sessions\n- Free returns within 30 days\n\n[View {product_name} Again]\n{product_url}\n\nNot sure? Check out our {product_category} collection for more options.\n\nHappy browsing!\nThe Team",
      ),

      n("6", "delayAction", "Wait 24 hours before follow-up", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      n("7", "condition", "Has user still not added to cart?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "view_count", operator: "greaterThan", value: "1" },
          ],
        },
      }),

      n(
        "8",
        "hitApi",
        "WebEngage: Send push with category recommendations",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/users/{user_email}/push",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "title",
                value: "New picks in {product_category} just for you",
              },
              {
                key: "message",
                value:
                  "We curated top {product_category} items based on your browsing. Explore now and find your perfect match!",
              },
              {
                key: "url",
                value:
                  "https://yourstore.com/collections/{product_category}?ref=browse_reminder",
              },
              { key: "ttl", value: "86400" },
            ],
            variables: [
              {
                name: "response.status",
                customName: "push_status",
                type: "string",
              },
              {
                name: "response.messageId",
                customName: "push_message_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Browse abandonment recovery for users who view products without adding to cart. AI analyzes browsing behavior and purchase intent, HubSpot checks customer history, then sends a personalized email with product details and social proof. After 24 hours, if no cart add, sends a WebEngage push notification with curated category recommendations.",
  },

  // ===================================================================
  // 6. PRICE DROP ALERT — Pattern D
  // ===================================================================
  "Price Drop Alert": {
    nodes: [
      trigger("WebEngage: Product Price Decreased", "webhook_app", {
        source: "WebEngage",
        event: "catalog.price_drop",
        howItStarts:
          "Fires when a product's price decreases by 10% or more, detected by catalog sync with WebEngage",
        setup:
          "In WebEngage Dashboard > Data Platform > Catalog, enable price change tracking. Create a webhook for 'Price Drop' events with a threshold of 10%. Ensure product_id, old_price, new_price, and interested user segments are included.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract price drop details and craft urgency messaging",
        280,
        "You are an ecommerce pricing alert specialist. Parse the price drop webhook, calculate savings, and generate compelling urgency-driven messaging for the alert.",
        "Extract from this WebEngage price drop webhook: {{{1.body}}}. Return JSON with: product_id, product_name, product_category, product_url, product_image_url, old_price (number), new_price (number), discount_percent (number), savings_amount (number), alert_headline (compelling headline), urgency_message (scarcity-driven one-liner).",
        [
          { field: "product_id", as: "product_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "old_price", as: "old_price", type: "number" },
          { field: "new_price", as: "new_price", type: "number" },
          { field: "discount_percent", as: "discount_percent", type: "number" },
          { field: "savings_amount", as: "savings_amount", type: "number" },
          { field: "alert_headline", as: "alert_headline", type: "string" },
          { field: "urgency_message", as: "urgency_message", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "WebEngage: Fetch users who viewed or wishlisted this product",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/segments/query",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "eventName", value: "Product Viewed" },
              {
                key: "filters",
                value:
                  '[{"attribute":"product_id","operator":"equals","value":"{product_id}"}]',
              },
              { key: "period", value: "30d" },
            ],
            variables: [
              {
                name: "segment.userCount",
                customName: "interested_user_count",
                type: "number",
              },
              {
                name: "segment.users[0].email",
                customName: "first_user_email",
                type: "string",
              },
              {
                name: "segment.users[0].firstName",
                customName: "first_user_name",
                type: "string",
              },
              { name: "segment.id", customName: "segment_id", type: "string" },
            ],
          },
        },
      ),

      n("4", "condition", "Is discount 20% or more (high urgency)?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "discount_percent",
              operator: "greaterThanOrEqual",
              value: "20",
            },
          ],
        },
      }),

      emailNode(
        "5",
        "SendGrid: Price drop alert email to interested users",
        1000,
        "{first_user_email}",
        "{alert_headline} — Save ${savings_amount} on {product_name}!",
        "Hi {first_user_name},\n\nGreat news! A product you've been watching just dropped in price:\n\n{product_name}\nWas: ${old_price} → Now: ${new_price}\nYou save: ${savings_amount} ({discount_percent}% off)\n\n{urgency_message}\n\n[Shop Now at the New Price]\n{product_url}\n\nThis price isn't guaranteed to last — {interested_user_count} other shoppers are watching this item too.\n\nHappy saving!\nThe Deals Team",
      ),

      n("6", "delayAction", "Wait 36 hours before urgency follow-up", 1240, {
        delayDetails: { duration: 36, unit: "hours" },
      }),

      n(
        "7",
        "condition",
        "Is the product still available and not purchased?",
        1480,
        {
          conditionDetails: {
            conditions: [
              {
                field: "interested_user_count",
                operator: "greaterThan",
                value: "0",
              },
            ],
          },
        },
      ),

      n(
        "8",
        "hitApi",
        "WebEngage: Send push notification with final urgency",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/users/{first_user_email}/push",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "title",
                value:
                  "Price alert: {product_name} still {discount_percent}% off",
              },
              {
                key: "message",
                value:
                  "Your watched item is still on sale at ${new_price}. {interested_user_count} others are eyeing it — grab yours before the price goes back up!",
              },
              { key: "url", value: "{product_url}?ref=price_drop_reminder" },
              { key: "ttl", value: "86400" },
            ],
            variables: [
              {
                name: "response.status",
                customName: "reminder_push_status",
                type: "string",
              },
              {
                name: "response.messageId",
                customName: "reminder_push_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Price drop alert workflow that detects catalog price decreases via WebEngage. AI calculates savings and generates urgency messaging, then queries for users who viewed or wishlisted the product. Sends an immediate email alert with savings details, waits 36 hours, and follows up with a push notification emphasizing scarcity if the user hasn't purchased.",
  },

  // ===================================================================
  // 7. BACK-IN-STOCK ALERT — Pattern D
  // ===================================================================
  "Back-in-Stock Alert": {
    nodes: [
      trigger("WebEngage: Product Restocked Event", "webhook_app", {
        source: "WebEngage",
        event: "catalog.back_in_stock",
        howItStarts:
          "Fires when a previously out-of-stock product is restocked, detected by inventory sync with WebEngage catalog",
        setup:
          "In WebEngage Dashboard > Data Platform > Catalog, enable inventory tracking. Create a webhook for 'Back in Stock' events. Ensure product_id, product_name, available_quantity, and waitlist data are included in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract restock details and generate alert copy",
        280,
        "You are an inventory alert specialist. Parse the back-in-stock webhook, extract product and availability details, and generate compelling messaging that drives immediate action from waitlisted customers.",
        "Extract from this WebEngage back-in-stock webhook: {{{1.body}}}. Return JSON with: product_id, product_name, product_category, product_price (number), product_url, product_image_url, available_quantity (number), days_out_of_stock (number), waitlist_count (number), alert_subject (email subject line), scarcity_message (urgency one-liner based on stock vs waitlist).",
        [
          { field: "product_id", as: "product_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          {
            field: "available_quantity",
            as: "available_quantity",
            type: "number",
          },
          { field: "waitlist_count", as: "waitlist_count", type: "number" },
          {
            field: "days_out_of_stock",
            as: "days_out_of_stock",
            type: "number",
          },
          { field: "alert_subject", as: "alert_subject", type: "string" },
          { field: "scarcity_message", as: "scarcity_message", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "WebEngage: Fetch waitlist subscribers for this product",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/segments/query",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "eventName", value: "Back in Stock Subscribed" },
              {
                key: "filters",
                value:
                  '[{"attribute":"product_id","operator":"equals","value":"{product_id}"}]',
              },
            ],
            variables: [
              {
                name: "segment.userCount",
                customName: "subscriber_count",
                type: "number",
              },
              {
                name: "segment.users[0].email",
                customName: "subscriber_email",
                type: "string",
              },
              {
                name: "segment.users[0].firstName",
                customName: "subscriber_name",
                type: "string",
              },
              {
                name: "segment.users[0].phone",
                customName: "subscriber_phone",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "condition",
        "Is stock limited (quantity < waitlist count)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "available_quantity",
                operator: "lessThan",
                value: "{waitlist_count}",
              },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "SendGrid: Back-in-stock alert to waitlisted customers",
        1000,
        "{subscriber_email}",
        "{alert_subject}",
        "Hi {subscriber_name},\n\nThe wait is over! {product_name} is back in stock after {days_out_of_stock} days.\n\nPrice: ${product_price}\nAvailable: {available_quantity} units\n\n{scarcity_message}\n\n[Shop Now — Before It Sells Out Again]\n{product_url}\n\n{waitlist_count} people were waiting for this item. First come, first served!\n\nBest,\nThe Inventory Team",
      ),

      n("6", "delayAction", "Wait 24 hours before purchase prompt", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      n("7", "condition", "Is product still in stock after 24h?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "available_quantity",
              operator: "greaterThan",
              value: "0",
            },
          ],
        },
      }),

      smsNode(
        "8",
        "WebEngage: SMS purchase nudge to waitlist subscribers",
        1720,
        "{subscriber_phone}",
        "Hi {subscriber_name}! {product_name} is still available at ${product_price} but going fast. Only {available_quantity} left. Shop now: {product_url}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Back-in-stock alert workflow that notifies waitlisted customers when products are restocked. AI generates scarcity-aware messaging based on available quantity vs waitlist size. Sends an immediate email to subscribers, waits 24 hours, checks remaining stock, then sends an SMS nudge to drive purchase if inventory is still available.",
  },

  // ===================================================================
  // 8. LOW INVENTORY URGENCY ALERT — Pattern D
  // ===================================================================
  "Low Inventory Urgency Alert": {
    nodes: [
      trigger("WebEngage: Inventory Below Threshold", "webhook_app", {
        source: "WebEngage",
        event: "catalog.low_stock",
        howItStarts:
          "Fires when a popular product's inventory drops below the configured threshold (e.g., 10 units), detected by WebEngage catalog sync",
        setup:
          "In WebEngage Dashboard > Data Platform > Catalog, set low-stock threshold alerts. Create a webhook for 'Low Inventory' events. Include product_id, current_stock, threshold, and recent buyer/viewer data.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract stock details and craft scarcity messaging",
        280,
        "You are a scarcity marketing specialist. Parse the low inventory webhook, extract current stock levels and product details, and generate high-urgency scarcity messaging that creates authentic FOMO without being manipulative.",
        "Extract from this WebEngage low inventory webhook: {{{1.body}}}. Return JSON with: product_id, product_name, product_category, product_price (number), product_url, current_stock (number), stock_threshold (number), recent_purchases_24h (number), scarcity_level (critical/low/moderate), urgency_headline (compelling headline), urgency_cta (action-driven CTA text).",
        [
          { field: "product_id", as: "product_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "current_stock", as: "current_stock", type: "number" },
          {
            field: "recent_purchases_24h",
            as: "recent_purchases_24h",
            type: "number",
          },
          { field: "scarcity_level", as: "scarcity_level", type: "string" },
          { field: "urgency_headline", as: "urgency_headline", type: "string" },
          { field: "urgency_cta", as: "urgency_cta", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "WebEngage: Find users who viewed or carted this product recently",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/segments/query",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "eventName", value: "Product Viewed" },
              {
                key: "filters",
                value:
                  '[{"attribute":"product_id","operator":"equals","value":"{product_id}"},{"attribute":"timestamp","operator":"greaterThan","value":"7d"}]',
              },
            ],
            variables: [
              {
                name: "segment.userCount",
                customName: "interested_users",
                type: "number",
              },
              {
                name: "segment.users[0].email",
                customName: "target_email",
                type: "string",
              },
              {
                name: "segment.users[0].firstName",
                customName: "target_name",
                type: "string",
              },
              {
                name: "segment.users[0].phone",
                customName: "target_phone",
                type: "string",
              },
            ],
          },
        },
      ),

      n("4", "condition", "Is stock critically low (5 or fewer units)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "current_stock", operator: "lessThanOrEqual", value: "5" },
          ],
        },
      }),

      n("5", "hitApi", "WebEngage: Send urgent push notification", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.webengage.com/v2/users/{target_email}/push",
          headers: [
            { key: "Authorization", value: "Bearer {webengage_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{urgency_headline}" },
            {
              key: "message",
              value:
                "Only {current_stock} left! {product_name} at ${product_price}. {recent_purchases_24h} sold in the last 24h. {urgency_cta}",
            },
            { key: "url", value: "{product_url}?ref=low_stock_alert" },
            { key: "priority", value: "high" },
            { key: "ttl", value: "43200" },
          ],
          variables: [
            {
              name: "response.status",
              customName: "push_sent_status",
              type: "string",
            },
            {
              name: "response.messageId",
              customName: "push_msg_id",
              type: "string",
            },
          ],
        },
      }),

      n("6", "delayAction", "Wait 8 hours before final alert", 1240, {
        delayDetails: { duration: 8, unit: "hours" },
      }),

      n(
        "7",
        "condition",
        "Is product still available (any stock left)?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { field: "current_stock", operator: "greaterThan", value: "0" },
            ],
          },
        },
      ),

      emailNode(
        "8",
        "SendGrid: Last chance email with scarcity proof",
        1720,
        "{target_email}",
        "Last chance: Only {current_stock} of {product_name} remaining",
        "Hi {target_name},\n\nThis is your final heads up — {product_name} is almost gone.\n\nCurrent stock: {current_stock} units\nSold in last 24h: {recent_purchases_24h}\nPrice: ${product_price}\n\nScarcity level: {scarcity_level}\n\n{interested_users} other shoppers have been watching this product. Once it's gone, there's no restock date confirmed.\n\n[{urgency_cta}]\n{product_url}\n\nDon't miss out this time.\n\nBest,\nThe Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Low inventory urgency alert that triggers when product stock drops below threshold. AI assesses scarcity level and generates appropriate urgency messaging, WebEngage identifies interested users, then sends an immediate push notification. After 8 hours, if stock remains, sends a final email with social proof showing recent purchase velocity.",
  },

  // ===================================================================
  // 9. FIRST PURCHASE COUPON REMINDER — Pattern A
  // ===================================================================
  "First Purchase Coupon Reminder": {
    nodes: [
      trigger("WebEngage: New User Signup With Welcome Coupon", "webhook_app", {
        source: "WebEngage",
        event: "user.signup_coupon_issued",
        howItStarts:
          "Fires when a new user signs up and receives a first-purchase welcome coupon, tracked by WebEngage events",
        setup:
          "In WebEngage Dashboard > Integrations > Webhooks, create a webhook for the 'Coupon Issued' custom event filtered to welcome/first-purchase coupons. Include user_id, email, coupon_code, discount_value, and expiry_date.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract coupon details and personalize reminder cadence",
        280,
        "You are a coupon marketing specialist. Parse the coupon issuance webhook, extract coupon details, and determine the optimal reminder timing and messaging based on discount value and expiry window.",
        "Extract from this WebEngage coupon issuance webhook: {{{1.body}}}. Return JSON with: user_email, user_name, coupon_code, discount_value (number), discount_type (percent/fixed), expiry_date, days_until_expiry (number), min_order_value (number), reminder_urgency (high/medium/low based on days left), personalized_hook (one-line motivational message).",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "coupon_code", as: "coupon_code", type: "string" },
          { field: "discount_value", as: "discount_value", type: "number" },
          { field: "discount_type", as: "discount_type", type: "string" },
          { field: "expiry_date", as: "expiry_date", type: "string" },
          {
            field: "days_until_expiry",
            as: "days_until_expiry",
            type: "number",
          },
          { field: "min_order_value", as: "min_order_value", type: "number" },
          {
            field: "personalized_hook",
            as: "personalized_hook",
            type: "string",
          },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Verify new customer status and preferences",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{user_email}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["email","firstname","num_associated_deals","hs_buying_role","favorite_category"]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.num_associated_deals",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "results[0].properties.favorite_category",
                customName: "preferred_category",
                type: "string",
              },
              {
                name: "results[0].properties.hs_object_id",
                customName: "contact_id",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "condition",
        "Has user already made a purchase (coupon unused)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "total_orders", operator: "equals", value: "0" },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "SendGrid: Coupon reminder with product recommendations",
        1000,
        "{user_email}",
        "{user_name}, your {discount_value}{discount_type} welcome coupon is waiting!",
        "Hi {user_name},\n\n{personalized_hook}\n\nDon't forget — you have a special welcome discount:\n\nCoupon code: {coupon_code}\nDiscount: {discount_value}{discount_type} off\nMin. order: ${min_order_value}\nExpires: {expiry_date} ({days_until_expiry} days left)\n\nPopular picks in {preferred_category} to get you started:\n- Bestseller #1 in {preferred_category}\n- Staff pick of the week\n- New arrival this month\n\n[Browse & Use Your Coupon]\n\nFirst-time shoppers also get free shipping on orders over $50.\n\nHappy shopping!\nThe Welcome Team",
      ),

      n("6", "delayAction", "Wait 3 days before final reminder", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      n("7", "condition", "Is coupon still unused with 2+ days left?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "days_until_expiry", operator: "greaterThan", value: "2" },
          ],
        },
      }),

      n(
        "8",
        "hitApi",
        "WebEngage: Send WhatsApp last-chance coupon reminder",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/users/{user_email}/whatsapp",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "templateName", value: "coupon_expiry_reminder" },
              {
                key: "parameters",
                value:
                  '["{user_name}","{coupon_code}","{discount_value}","{expiry_date}"]',
              },
            ],
            variables: [
              {
                name: "response.status",
                customName: "whatsapp_status",
                type: "string",
              },
              {
                name: "response.messageId",
                customName: "whatsapp_msg_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
    summary:
      "First purchase coupon reminder journey for new signups. AI extracts coupon details and calculates urgency based on expiry, HubSpot verifies no purchase has been made yet, then sends an email reminder with product recommendations in the user's preferred category. After 3 days, sends a WhatsApp last-chance reminder before the coupon expires.",
  },

  // ===================================================================
  // 10. POST-PURCHASE THANK YOU — Pattern E
  // ===================================================================
  "Post-Purchase Thank You": {
    nodes: [
      trigger("WebEngage: Order Placed Event", "webhook_app", {
        source: "WebEngage",
        event: "order.placed",
        howItStarts:
          "Fires when a customer completes a purchase, captured by WebEngage transaction tracking or Shopify integration",
        setup:
          "In WebEngage Dashboard > Integrations > Webhooks, create a webhook for the 'Checkout Completed' or 'Revenue' event. Ensure order_id, user_id, line_items, order_total, and payment method are included.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract order details and generate thank you content",
        280,
        "You are a post-purchase experience specialist. Parse the order webhook, extract purchase details, and generate warm thank-you messaging with relevant product usage tips based on what was ordered.",
        "Extract from this WebEngage order placed webhook: {{{1.body}}}. Return JSON with: user_email, user_name, order_id, order_total (number), item_count (number), primary_product_name (most expensive item), primary_product_category, payment_method, thank_you_message (warm personalized message), usage_tip (one product care/usage tip relevant to the primary product).",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          { field: "item_count", as: "item_count", type: "number" },
          {
            field: "primary_product_name",
            as: "primary_product_name",
            type: "string",
          },
          {
            field: "primary_product_category",
            as: "primary_product_category",
            type: "string",
          },
          {
            field: "thank_you_message",
            as: "thank_you_message",
            type: "string",
          },
          { field: "usage_tip", as: "usage_tip", type: "string" },
        ],
      ),

      n("3", "hitApi", "HubSpot: Lookup customer tenure and VIP status", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{user_email}"}]}]',
            },
            {
              key: "properties",
              value:
                '["email","firstname","num_associated_deals","total_revenue","createdate","hs_lead_status"]',
            },
          ],
          variables: [
            {
              name: "results[0].properties.num_associated_deals",
              customName: "total_orders",
              type: "number",
            },
            {
              name: "results[0].properties.total_revenue",
              customName: "lifetime_revenue",
              type: "string",
            },
            {
              name: "results[0].properties.createdate",
              customName: "customer_since",
              type: "string",
            },
            {
              name: "results[0].properties.hs_lead_status",
              customName: "vip_status",
              type: "string",
            },
          ],
        },
      }),

      n("4", "condition", "Is this the customer's first purchase?", 760, {
        conditionDetails: {
          conditions: [
            { field: "total_orders", operator: "lessThanOrEqual", value: "1" },
          ],
        },
      }),

      emailNode(
        "5",
        "SendGrid: Personalized thank you email with usage tips",
        1000,
        "{user_email}",
        "Thank you for your order, {user_name}! Order #{order_id} confirmed",
        "Hi {user_name},\n\n{thank_you_message}\n\nOrder Summary:\nOrder #: {order_id}\nItems: {item_count}\nTotal: ${order_total}\n\nYour featured item: {primary_product_name}\n\nPro tip: {usage_tip}\n\nWhat's next:\n- Track your shipment — we'll email you when it ships\n- Explore care tips for {primary_product_category} products\n- Join our community for exclusive tips and offers\n\nCustomer since: {customer_since}\nTotal orders: {total_orders}\n\nThank you for choosing us!\nThe Team",
      ),

      n(
        "6",
        "delayAction",
        "Wait 7 days for delivery and product experience",
        1240,
        {
          delayDetails: { duration: 7, unit: "days" },
        },
      ),

      n("7", "condition", "Was order high value (over $150)?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "order_total", operator: "greaterThan", value: "150" },
          ],
        },
      }),

      emailNode(
        "8",
        "SendGrid: Follow-up with product care guide and support offer",
        1720,
        "{user_email}",
        "How's your {primary_product_name}? Here are some tips to love it even more",
        "Hi {user_name},\n\nIt's been a week since your order — we hope you're loving your {primary_product_name}!\n\nHere are some tips to get the most out of your purchase:\n\n1. {usage_tip}\n2. Store in a cool, dry place for maximum longevity\n3. Check out our {primary_product_category} care guide for advanced tips\n\n[Read the Complete Care Guide]\n\nHaving any issues? Our support team is here to help — reply to this email or call us at 1-800-SUPPORT.\n\nWe'd also love to hear about your experience. Stay tuned for a quick feedback request!\n\nBest,\nThe Customer Success Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Post-purchase thank you journey triggered by WebEngage order events. AI extracts order details and generates personalized usage tips, HubSpot checks customer tenure and VIP status, then sends a warm thank-you email with product care advice. After 7 days, follows up with an extended care guide and proactive support offer.",
  },

  // ===================================================================
  // 11. REVIEW REQUEST JOURNEY — Pattern E
  // ===================================================================
  "Review Request Journey": {
    nodes: [
      trigger("WebEngage: Order Delivered Event", "webhook_app", {
        source: "WebEngage",
        event: "order.delivered",
        howItStarts:
          "Fires when an order is marked as delivered, tracked by shipping integration or manual status update synced to WebEngage",
        setup:
          "In WebEngage Dashboard > Integrations > Webhooks, create a webhook for the 'Order Delivered' custom event. Include order_id, user_id, delivery_date, and product details in the payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract delivery details and craft review request",
        280,
        "You are a customer review specialist. Parse the order delivered webhook, extract product and delivery details, and generate a friendly non-pushy review request message that maximizes response rate.",
        "Extract from this WebEngage order delivered webhook: {{{1.body}}}. Return JSON with: user_email, user_name, order_id, primary_product_name, primary_product_category, product_url, delivery_date, days_since_order (number), review_subject (friendly email subject), review_prompt (warm one-liner asking for honest feedback), review_url (constructed review page URL).",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          {
            field: "primary_product_name",
            as: "primary_product_name",
            type: "string",
          },
          {
            field: "primary_product_category",
            as: "primary_product_category",
            type: "string",
          },
          { field: "product_url", as: "product_url", type: "string" },
          { field: "review_subject", as: "review_subject", type: "string" },
          { field: "review_prompt", as: "review_prompt", type: "string" },
          { field: "review_url", as: "review_url", type: "string" },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Check customer tenure and past review history",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{user_email}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["email","firstname","num_associated_deals","total_revenue","last_review_date","reviews_submitted"]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.num_associated_deals",
                customName: "total_orders",
                type: "number",
              },
              {
                name: "results[0].properties.total_revenue",
                customName: "lifetime_value",
                type: "string",
              },
              {
                name: "results[0].properties.reviews_submitted",
                customName: "past_reviews",
                type: "number",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "condition",
        "Is this a first-time buyer (no prior reviews)?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "past_reviews", operator: "equals", value: "0" },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "SendGrid: Friendly review request email",
        1000,
        "{user_email}",
        "{review_subject}",
        "Hi {user_name},\n\nYour {primary_product_name} was delivered — we hope you love it!\n\n{review_prompt}\n\nYour review helps other shoppers make informed decisions and helps us improve. It only takes 2 minutes:\n\n[Leave a Review for {primary_product_name}]\n{review_url}\n\nQuick rating: How would you rate your experience from 1-5 stars?\n\nOrder: #{order_id}\nProduct: {primary_product_name}\nCategory: {primary_product_category}\n\nThank you for your time!\nThe Team",
      ),

      n("6", "delayAction", "Wait 5 days for review submission", 1240, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      n("7", "condition", "Has customer submitted a review?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "past_reviews", operator: "equals", value: "0" },
          ],
        },
      }),

      n(
        "8",
        "hitApi",
        "WebEngage: Send push notification review reminder",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/users/{user_email}/push",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "title",
                value: "Quick question about your {primary_product_name}",
              },
              {
                key: "message",
                value:
                  "How's it going with your purchase? Tap to leave a quick review — it takes under 60 seconds and helps fellow shoppers!",
              },
              { key: "url", value: "{review_url}?ref=push_reminder" },
              { key: "ttl", value: "172800" },
            ],
            variables: [
              {
                name: "response.status",
                customName: "reminder_push_status",
                type: "string",
              },
              {
                name: "response.messageId",
                customName: "reminder_push_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Review request journey triggered when WebEngage detects order delivery. AI generates friendly review messaging tailored to the product, HubSpot checks past review history, then sends an email review request. After 5 days without a review, sends a gentle push notification reminder to maximize review collection rates.",
  },

  // ===================================================================
  // 12. CROSS-SELL AFTER PURCHASE — Pattern E
  // ===================================================================
  "Cross-Sell After Purchase": {
    nodes: [
      trigger("WebEngage: Purchase Completed Event", "webhook_app", {
        source: "WebEngage",
        event: "order.completed",
        howItStarts:
          "Fires when a customer completes a purchase, triggering a cross-sell recommendation flow based on the purchased items",
        setup:
          "In WebEngage Dashboard > Integrations > Webhooks, create a webhook for the 'Revenue' or 'Checkout Completed' event. Include order_id, product details, category, order_total, and user profile data.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract purchase details and generate cross-sell recommendations",
        280,
        "You are an ecommerce cross-sell specialist. Parse the purchase webhook, analyze the ordered products, and generate highly relevant complementary product recommendations with persuasive copy.",
        "Extract from this WebEngage purchase webhook: {{{1.body}}}. Return JSON with: user_email, user_name, order_id, order_total (number), primary_product_name, primary_product_category, cross_sell_product_1 (complementary product name), cross_sell_product_2 (second recommendation), cross_sell_reason (why these products complement the purchase), cross_sell_subject (email subject for cross-sell), bundle_discount_suggestion (suggested bundle offer).",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "order_id", as: "order_id", type: "string" },
          { field: "order_total", as: "order_total", type: "number" },
          {
            field: "primary_product_name",
            as: "primary_product_name",
            type: "string",
          },
          {
            field: "primary_product_category",
            as: "primary_product_category",
            type: "string",
          },
          {
            field: "cross_sell_product_1",
            as: "cross_sell_product_1",
            type: "string",
          },
          {
            field: "cross_sell_product_2",
            as: "cross_sell_product_2",
            type: "string",
          },
          {
            field: "cross_sell_reason",
            as: "cross_sell_reason",
            type: "string",
          },
          {
            field: "cross_sell_subject",
            as: "cross_sell_subject",
            type: "string",
          },
          {
            field: "bundle_discount_suggestion",
            as: "bundle_discount_suggestion",
            type: "string",
          },
        ],
      ),

      n(
        "3",
        "hitApi",
        "HubSpot: Lookup customer purchase history and LTV",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{user_email}"}]}]',
              },
              {
                key: "properties",
                value:
                  '["email","firstname","num_associated_deals","total_revenue","hs_buying_role","recent_deal_amount"]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.num_associated_deals",
                customName: "total_purchases",
                type: "number",
              },
              {
                name: "results[0].properties.total_revenue",
                customName: "customer_ltv",
                type: "string",
              },
              {
                name: "results[0].properties.hs_buying_role",
                customName: "buyer_type",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "condition",
        "Is this a first-time buyer (prime for cross-sell)?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "total_purchases",
                operator: "lessThanOrEqual",
                value: "1",
              },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "SendGrid: Cross-sell recommendation email",
        1000,
        "{user_email}",
        "{cross_sell_subject}",
        "Hi {user_name},\n\nThanks for your recent purchase of {primary_product_name}! Here are some items that go perfectly with it:\n\nRecommended for you:\n\n1. {cross_sell_product_1}\n   {cross_sell_reason}\n\n2. {cross_sell_product_2}\n   Complete your {primary_product_category} setup\n\n{bundle_discount_suggestion}\n\n[Shop Recommended Products]\n\nCustomers who bought {primary_product_name} also loved these picks — 87% satisfaction rate!\n\nOrder #{order_id} | Total: ${order_total}\n\nHappy shopping!\nThe Recommendations Team",
      ),

      n("6", "delayAction", "Wait 10 days for cross-sell consideration", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),

      n("7", "condition", "Has customer made a follow-up purchase?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "total_purchases",
              operator: "lessThanOrEqual",
              value: "1",
            },
          ],
        },
      }),

      n(
        "8",
        "hitApi",
        "WebEngage: Send push with personalized cross-sell offer",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/users/{user_email}/push",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "title",
                value: "Complete your {primary_product_category} collection",
              },
              {
                key: "message",
                value:
                  "Pair {cross_sell_product_1} with your {primary_product_name} for the ultimate setup. {bundle_discount_suggestion}",
              },
              {
                key: "url",
                value:
                  "https://yourstore.com/collections/{primary_product_category}?ref=cross_sell",
              },
              { key: "ttl", value: "172800" },
            ],
            variables: [
              {
                name: "response.status",
                customName: "cross_sell_push_status",
                type: "string",
              },
              {
                name: "response.messageId",
                customName: "cross_sell_push_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Post-purchase cross-sell workflow triggered by WebEngage order completion events. AI analyzes the purchase and generates complementary product recommendations with bundle discount suggestions. HubSpot checks buyer history and LTV. Sends a cross-sell email immediately, then follows up with a push notification after 10 days if no repeat purchase is detected.",
  },

  // ===================================================================
  // 13. REPLENISHMENT REMINDER — Pattern F
  // ===================================================================
  "Replenishment Reminder": {
    nodes: [
      trigger("WebEngage: Predicted Reorder Date Approaching", "webhook_app", {
        source: "WebEngage",
        event: "user.reorder_predicted",
        howItStarts:
          "Fires when a customer's predicted reorder date is approaching based on past purchase frequency, calculated by WebEngage predictive analytics",
        setup:
          "In WebEngage Dashboard > Predictive Segments > Reorder Predictions, configure a webhook that fires 5 days before the predicted reorder date. Include user_id, product_id, last_purchase_date, predicted_reorder_date, and average_reorder_interval.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract replenishment details and generate reorder messaging",
        280,
        "You are a replenishment marketing specialist. Parse the predicted reorder webhook, extract product and timing details, and generate compelling reorder messaging that emphasizes convenience and continuity.",
        "Extract from this WebEngage predicted reorder webhook: {{{1.body}}}. Return JSON with: user_email, user_name, product_name, product_category, product_price (number), product_url, last_purchase_date, predicted_reorder_date, days_until_reorder (number), reorder_interval_days (number), reorder_subject (email subject), convenience_message (one-liner about easy reordering).",
        [
          { field: "user_email", as: "user_email", type: "string" },
          { field: "user_name", as: "user_name", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "product_category", as: "product_category", type: "string" },
          { field: "product_price", as: "product_price", type: "number" },
          { field: "product_url", as: "product_url", type: "string" },
          {
            field: "last_purchase_date",
            as: "last_purchase_date",
            type: "string",
          },
          {
            field: "predicted_reorder_date",
            as: "predicted_reorder_date",
            type: "string",
          },
          {
            field: "days_until_reorder",
            as: "days_until_reorder",
            type: "number",
          },
          {
            field: "reorder_interval_days",
            as: "reorder_interval_days",
            type: "number",
          },
          { field: "reorder_subject", as: "reorder_subject", type: "string" },
          {
            field: "convenience_message",
            as: "convenience_message",
            type: "string",
          },
        ],
      ),

      n(
        "3",
        "hitApi",
        "Shopify: Check current price and stock for reorder item",
        520,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products/{product_name}.json",
            headers: [
              { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            ],
            variables: [
              {
                name: "product.variants[0].price",
                customName: "current_price",
                type: "string",
              },
              {
                name: "product.variants[0].inventory_quantity",
                customName: "current_stock",
                type: "number",
              },
              {
                name: "product.title",
                customName: "verified_product_name",
                type: "string",
              },
              {
                name: "product.variants[0].id",
                customName: "variant_id",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "condition",
        "Is the product running low or at risk of stockout?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "current_stock", operator: "lessThan", value: "20" },
            ],
          },
        },
      ),

      emailNode(
        "5",
        "SendGrid: Replenishment reminder with reorder link",
        1000,
        "{user_email}",
        "{reorder_subject}",
        "Hi {user_name},\n\nBased on your last purchase of {product_name} on {last_purchase_date}, it might be time to stock up again!\n\n{convenience_message}\n\nYour product details:\n- Product: {verified_product_name}\n- Last price: ${product_price}\n- Current price: ${current_price}\n- Stock status: {current_stock} units available\n\nYour typical reorder cycle: every {reorder_interval_days} days\nSuggested reorder by: {predicted_reorder_date}\n\n[Reorder Now — One Click]\n{product_url}?variant={variant_id}&reorder=true\n\nSet up auto-replenishment and never run out again. Subscribe & save 15%!\n\n[Set Up Auto-Replenishment]\n\nBest,\nThe Replenishment Team",
      ),

      n("6", "delayAction", "Wait 4 days before follow-up reminder", 1240, {
        delayDetails: { duration: 4, unit: "days" },
      }),

      n("7", "condition", "Has customer reordered yet?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "days_until_reorder",
              operator: "lessThanOrEqual",
              value: "1",
            },
          ],
        },
      }),

      n(
        "8",
        "hitApi",
        "WebEngage: Send SMS reorder nudge with direct link",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.webengage.com/v2/users/{user_email}/sms",
            headers: [
              { key: "Authorization", value: "Bearer {webengage_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "message",
                value:
                  "Hi {user_name}! Time to restock your {product_name} (${current_price}). {current_stock} left in stock. One-tap reorder: {product_url}?variant={variant_id}&reorder=true — or subscribe to save 15%!",
              },
              { key: "senderName", value: "YourStore" },
            ],
            variables: [
              {
                name: "response.status",
                customName: "sms_reorder_status",
                type: "string",
              },
              {
                name: "response.messageId",
                customName: "sms_reorder_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Replenishment reminder workflow driven by WebEngage predictive reorder analytics. AI extracts purchase history and timing, Shopify verifies current price and stock availability, then sends a personalized email with one-click reorder and subscription upsell. After 4 days, if no reorder is detected, sends an SMS nudge with a direct reorder link and subscribe-and-save offer.",
  },
};
